import {
	Alert,
	Badge,
	Box,
	Button,
	Col,
	Container,
	Divider,
	Grid,
	Heading,
	Icons,
	Inline,
	Spinner,
	Stack,
	TabContent,
	Tabs,
	TabsList,
	Text,
} from "@sembark-travel/ui-base"
import {
	arrayMutators,
	FieldArray,
	Form,
	GetFieldValue,
	SelectField,
	SelectInputField,
	SwitchInputField,
	TextAreaInputField,
	TextInputField,
	useForm,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import {
	Breadcrumbs,
	ButtonLink,
	Link,
	searchToQuery,
} from "@sembark-travel/ui-router"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import { Helmet } from "react-helmet-async"
import { useEffect, useMemo, useState } from "react"
import * as Validator from "yup"
import {
	SelectWAFlow,
	SelectWAFlowScreen,
	SelectWAPhoneNumber,
	SelectWATemplate,
	WATemplatePreviewBubble,
	waTemplateSourceMismatch,
} from "../Integrations"
import type { TWAFlow, TWAFlowScreen, TWATemplate } from "../Integrations"
import { SelectWAQuickSend, type TWAQuickSend } from "../WhatsAppQuickSends"
import { generatePath, useLocation, useNavigate } from "../router-utils"
import { AutomationDefinitionForm } from "./DefinitionDialog"
import {
	CheckpointsField,
	IntervalWindowField,
	SendWindowField,
} from "./CheckpointsField"
import { RuleGroupEditor } from "./RuleGroupEditor"
import {
	buildRuleGroup,
	createRuleGroupFormValue,
	ruleGroupFormValueFromGroup,
	validateRuleGroupFormValue,
	type TAutomationRuleGroupFormValue,
} from "./definition-form"

import {
	TAutomationTemplate,
	TAutomationTemplateAutomation,
	TAutomationTemplateSummary,
	type TAutomationTemplateField,
	TAutomationTemplatePayload,
	TAutomationTemplatePhoneNumberOption,
	TAutomationTemplateWhatsAppTemplateOption,
	XHR,
	type TAutomationSourceField,
	type TAutomationSourceVariable,
	type TAutomationMessageKind,
	type TAutomationTemplateMessageChannel,
	type TAutomationCheckpointFormValue,
	type TAutomationEveryFormValue,
	type TAutomationOffsetFormValue,
	createEveryFormValue,
	createOffsetFormValue,
	everyToString,
	offsetToString,
	offsetsToCheckpoints,
	checkpointsToOffsets,
	parseEveryString,
	parseOffsetString,
} from "./store"

type TSelectedWATemplate =
	| TWATemplate
	| TAutomationTemplateWhatsAppTemplateOption

type TTemplateMessageFormValue = {
	// What this message is. Absent means a template, which is all a recipe
	// offers unless it fires on something the customer just did.
	kind?: TAutomationMessageKind
	whatsapp_template?: TSelectedWATemplate
	// A free-text reply.
	body?: string
	// A form offered: the Flow, the bubble around its button, and the screen
	// it opens on.
	flow?: TWAFlow | null
	flow_body?: string
	flow_cta?: string
	flow_screen?: TWAFlowScreen | null
	flow_header?: string
	flow_footer?: string
	// A saved quick send, as its picker holds it.
	quick_send?: Pick<TWAQuickSend, "id" | "name"> | null
	enable_wait?: boolean
	wait_before?: TAutomationOffsetFormValue
}

const MESSAGE_KIND_LABELS: Record<TAutomationMessageKind, string> = {
	template: "Approved template",
	text: "Message you write",
	flow: "A form to fill in",
	quick_send: "A quick send",
}

function messageKind(
	message?: TTemplateMessageFormValue | null
): TAutomationMessageKind {
	return message?.kind ?? "template"
}

/**
 * The kinds a recipe's messages may be. Templates only unless the recipe
 * says otherwise.
 */
function templateMessageKinds(
	template?: TAutomationTemplate | null
): Array<TAutomationMessageKind> {
	return (
		template?.configurable_fields?.find((field) => field.key === "messages")
			?.message_kinds ?? ["template"]
	)
}

/** Whether a message row carries everything its own kind needs. */
function messageIsComplete(message: TTemplateMessageFormValue): boolean {
	switch (messageKind(message)) {
		case "text":
			return Boolean(message.body?.trim())
		case "flow":
			return Boolean(
				message.flow?.id &&
				message.flow_body?.trim() &&
				message.flow_cta?.trim() &&
				message.flow_screen?.id
			)
		case "quick_send":
			return Boolean(message.quick_send?.id)
		default:
			return Boolean(message.whatsapp_template)
	}
}

/** One message as the recipe takes it: a channel entry naming its kind. */
function messageChannel(
	message: TTemplateMessageFormValue
): TAutomationTemplateMessageChannel {
	const kind = messageKind(message)

	if (kind === "text") {
		return {
			channel: "whatsapp" as const,
			kind,
			body: message.body?.trim(),
		}
	}

	if (kind === "flow") {
		return {
			channel: "whatsapp" as const,
			kind,
			flow_id: Number(message.flow?.id),
			flow_body: message.flow_body?.trim(),
			flow_cta: message.flow_cta?.trim(),
			flow_screen: message.flow_screen?.id,
			flow_header: message.flow_header?.trim() || undefined,
			flow_footer: message.flow_footer?.trim() || undefined,
		}
	}

	if (kind === "quick_send") {
		return {
			channel: "whatsapp" as const,
			kind,
			quick_send_id: Number(message.quick_send?.id),
		}
	}

	return {
		channel: "whatsapp" as const,
		kind,
		whatsapp_template_id: Number(message.whatsapp_template?.id),
	}
}

type TTemplateFormValues = {
	whatsapp_template?: TSelectedWATemplate
	phone_number?: TAutomationTemplatePhoneNumberOption
	checkpoints?: Array<TAutomationCheckpointFormValue>
	enable_interval_window?: boolean
	interval_start_offset?: TAutomationOffsetFormValue
	interval_end_offset?: TAutomationOffsetFormValue
	interval_every?: TAutomationEveryFormValue
	send_window?: { from?: string; to?: string }
	messages?: Array<TTemplateMessageFormValue>
	filters?: TAutomationRuleGroupFormValue
	// Values of the recipe's plain text fields, keyed by field.
	inputs?: Record<string, string>
	// Values of the recipe's on/off options, keyed by field.
	toggles?: Record<string, boolean>
	// The picked Flow behind a `whatsapp_flow` field, for display; the id
	// lives in `inputs`.
	flows?: Record<string, TWAFlow | null>
	// Same, for a `whatsapp_template` field the recipe filters on.
	waTemplates?: Record<string, TSelectedWATemplate | null>
}

/**
 * What the operator may type into a message they write themselves. A
 * template names its variables when it is created, so its own editor lists
 * them; a free-form reply has nowhere else to read them from.
 */
function AvailableVariables({
	variables,
}: {
	variables: Array<TAutomationSourceVariable>
}) {
	if (!variables.length) {
		return null
	}

	return (
		<Stack gap="1">
			<Text fontSize="sm" color="muted">
				Variables you can use, replaced when the message goes out:
			</Text>
			<Inline gap="2" flexWrap="wrap">
				{variables.map((variable) => (
					<Inline key={variable.key} gap="1" alignItems="center">
						<Badge>{variable.placeholder}</Badge>
						<Text fontSize="sm" color="muted">
							{variable.label}
						</Text>
					</Inline>
				))}
			</Inline>
		</Stack>
	)
}

/**
 * Clear every message whose template or form belongs to another business
 * account than the one now picked. One conversation is on one account, so a
 * mix of two is refused when it is saved.
 */
function clearMessagesOfOtherAccounts(
	form: { change: (name: string, value: unknown) => void },
	messages: Array<TTemplateMessageFormValue>,
	accountId?: number
) {
	if (!accountId) {
		return
	}

	messages.forEach((message, index) => {
		const templateAccountId = message.whatsapp_template?.wa_business_account_id

		if (index > 0 && templateAccountId && templateAccountId !== accountId) {
			form.change(`messages[${index}].whatsapp_template`, undefined)
		}

		if (
			message.flow?.wa_business_account?.id &&
			message.flow.wa_business_account.id !== accountId
		) {
			form.change(`messages[${index}].flow`, null)
			form.change(`messages[${index}].flow_screen`, null)
		}
	})
}

/**
 * The business account the messages already name. A template and a Flow each
 * belong to one, and a reply can only carry one the receiving number's
 * account owns — so once the first message names an account, every other
 * picker is narrowed to it. Falls back to the sender's account on a recipe
 * that still picks one.
 */
function messageBusinessAccountId(
	values: TTemplateFormValues
): number | undefined {
	for (const message of values.messages || []) {
		const accountId =
			message.whatsapp_template?.wa_business_account_id ??
			message.flow?.wa_business_account?.id

		if (accountId) {
			return accountId
		}
	}

	return values.phone_number?.wa_business_account_id ?? undefined
}

/**
 * The recipe's plain text fields, in declaration order. Rendered generically:
 * the recipe decides what they mean.
 */
function templateTextFields(
	template?: TAutomationTemplate | null
): Array<TAutomationTemplateField> {
	return (template?.configurable_fields || []).filter(
		(field) =>
			field.type === "text" ||
			field.type === "textarea" ||
			field.type === "whatsapp_flow" ||
			field.type === "whatsapp_template"
	)
}

/**
 * Conditions almost every automation of a recipe needs, drawn above the rule
 * builder instead of among the recipe's other inputs. They are ordinary
 * inputs — validated and submitted with the rest, and folded into the same
 * filter group by the recipe — so only where they appear differs.
 */
const PROMOTED_FILTER_FIELD_KEYS = ["replied_to_template_id", "button"]

function templatePromotedFilterFields(
	template?: TAutomationTemplate | null
): Array<TAutomationTemplateField> {
	return templateTextFields(template).filter((field) =>
		PROMOTED_FILTER_FIELD_KEYS.includes(field.key)
	)
}

/**
 * The quick replies on a picked template, in the order they read on it. A
 * link or a call button cannot be tapped into a reply, so neither is offered.
 */
function quickReplyLabels(
	waTemplate?: TSelectedWATemplate | null
): Array<string> {
	return (waTemplate?.preview?.buttons || [])
		.filter(
			(button) => String(button.type || "").toLowerCase() === "quick_reply"
		)
		.map((button) => String(button.text || "").trim())
		.filter((text) => text !== "")
}

/**
 * A field naming one of another field's quick replies. Until that template is
 * picked there is nothing to list, so it takes the text typed instead — as it
 * also does for a label the template no longer carries.
 */
function QuickReplyField({
	field,
	waTemplate,
	value,
}: {
	field: TAutomationTemplateField
	waTemplate?: TSelectedWATemplate | null
	value: string
}) {
	const labels = quickReplyLabels(waTemplate)

	if (!labels.length) {
		return (
			<TextInputField
				name={`inputs.${field.key}`}
				label={field.label}
				placeholder={field.placeholder}
				required={field.required}
				help={field.description}
			/>
		)
	}

	return (
		<SelectInputField
			name={`inputs.${field.key}`}
			label={field.label}
			required={field.required}
			help={field.description}
		>
			<option value="">Any button on the template</option>
			{labels.map((label) => (
				<option key={label} value={label}>
					{label}
				</option>
			))}
			{value && !labels.includes(value) ? (
				<option value={value}>{value}</option>
			) : null}
		</SelectInputField>
	)
}

/** The recipe's own inputs, minus the ones drawn with the conditions. */
function templateDetailFields(
	template?: TAutomationTemplate | null
): Array<TAutomationTemplateField> {
	return templateTextFields(template).filter(
		(field) => !PROMOTED_FILTER_FIELD_KEYS.includes(field.key)
	)
}

/**
 * The recipe's on/off options, in declaration order. Drawn as switches among
 * the details and sent as 1 or 0.
 */
function templateBooleanFields(
	template?: TAutomationTemplate | null
): Array<TAutomationTemplateField> {
	return (template?.configurable_fields || []).filter(
		(field) => field.type === "boolean"
	)
}

/**
 * Whether the recipe sends a WhatsApp template of its own. Every recipe did
 * until the ones that only create a record; those have no template to pick
 * and no sender to choose.
 */
function templateSendsWhatsAppTemplate(
	template?: TAutomationTemplate | null
): boolean {
	return Boolean(
		template?.configurable_fields.some(
			(field) =>
				field.key === "messages" || field.key === "whatsapp_template_id"
		)
	)
}

function templateNeedsPhoneNumber(
	template?: TAutomationTemplate | null
): boolean {
	return Boolean(
		template?.configurable_fields.some(
			(field) => field.key === "phone_number_id"
		)
	)
}

function templateSupportsFilters(
	template?: TAutomationTemplate | null
): boolean {
	return Boolean(
		template?.configurable_fields.some((field) => field.key === "filters")
	)
}

function templateSupportsMessageSequence(
	template?: TAutomationTemplate | null
): boolean {
	return Boolean(
		template?.configurable_fields.some((field) => field.key === "messages")
	)
}

function templateSchema(template?: TAutomationTemplate | null) {
	const requiresCheckpoints = template?.configurable_fields.some(
		(field) => field.key === "checkpoints"
	)
	const supportsMessageSequence = templateSupportsMessageSequence(template)
	const sendsWhatsAppTemplate = templateSendsWhatsAppTemplate(template)
	const needsPhoneNumber = templateNeedsPhoneNumber(template)
	const textFields = templateTextFields(template)

	return Validator.object().shape({
		// Sequence templates pick a template per message row instead of a single
		// top-level one, so the top-level picker is only required otherwise —
		// and not at all for a recipe that sends no template.
		whatsapp_template:
			supportsMessageSequence || !sendsWhatsAppTemplate
				? Validator.mixed().nullable()
				: Validator.object().required(
						"Please select an approved WhatsApp template"
					),
		phone_number: needsPhoneNumber
			? Validator.object().required("Please select a sender phone number")
			: Validator.mixed().nullable(),
		inputs: Validator.object().shape(
			Object.fromEntries(
				textFields.map((field) => [
					field.key,
					field.required
						? Validator.string()
								.trim()
								.required(`Please enter ${field.label.toLowerCase()}`)
						: Validator.string().nullable(),
				])
			)
		),
		messages: supportsMessageSequence
			? Validator.array()
					.required("Please add at least one message")
					.min(1, "Please add at least one message")
					.test(
						"messages-valid",
						"Every message needs its content filled in, and any wait must be a positive amount.",
						(value) => {
							const rows =
								(value as Array<TTemplateMessageFormValue> | undefined) || []

							return (
								rows.length > 0 &&
								rows.every((row) => {
									if (!messageIsComplete(row)) {
										return false
									}
									if (!row.enable_wait) {
										return true
									}
									// When a wait is enabled it must move forward.
									const wait = offsetToString(row.wait_before)

									return Boolean(wait && wait.startsWith("+"))
								})
							)
						}
					)
			: Validator.array().nullable(),
		checkpoints: requiresCheckpoints
			? Validator.array()
					.required("Please provide at least one checkpoint")
					.test(
						"has-checkpoints",
						"Please provide at least one checkpoint",
						(value) =>
							checkpointsToOffsets(
								value as Array<TAutomationCheckpointFormValue> | undefined
							).length > 0
					)
			: Validator.array().nullable(),
		// Only enforced when the operator turns on the optional repeat window: a
		// valid "from" / "until" offset and a positive "every" interval.
		interval_start_offset: Validator.mixed().when("enable_interval_window", {
			is: true,
			then: (schema) =>
				schema.test(
					"valid-start-offset",
					"Set a valid repeat-from offset.",
					(value) =>
						Boolean(offsetToString(value as TAutomationOffsetFormValue))
				),
		}),
		interval_end_offset: Validator.mixed().when("enable_interval_window", {
			is: true,
			then: (schema) =>
				schema.test(
					"valid-end-offset",
					"Set a valid repeat-until offset.",
					(value) =>
						Boolean(offsetToString(value as TAutomationOffsetFormValue))
				),
		}),
		interval_every: Validator.mixed().when("enable_interval_window", {
			is: true,
			then: (schema) =>
				schema.test("valid-every", "Set a valid repeat interval.", (value) =>
					Boolean(everyToString(value as TAutomationEveryFormValue))
				),
		}),
		send_window: Validator.mixed().test(
			"valid-send-window",
			"Set a send window with a start before its end.",
			(value) => {
				const window = value as { from?: string; to?: string } | undefined
				// Both bounds are required; "HH:MM" strings compare correctly
				// lexicographically.
				return Boolean(window?.from && window?.to && window.from < window.to)
			}
		),
		// A half-written rule cannot be saved, but no rules at all is fine —
		// that just means the automation runs for every record.
		filters: Validator.mixed().test(
			"complete-rules",
			"Every condition needs a value.",
			(value) =>
				Object.keys(
					collectRuleErrors(value as TAutomationRuleGroupFormValue | undefined)
				).length === 0
		),
	})
}

function collectRuleErrors(group: TAutomationRuleGroupFormValue | undefined) {
	const errors: Record<string, string> = {}
	validateRuleGroupFormValue(group, "filters", errors)

	return errors
}

function buildInitialValues(
	template: TAutomationTemplate | TAutomationTemplateAutomation,
	derived?: TAutomationTemplateAutomation["input"] | null
): TTemplateFormValues {
	// Edit: prefer the operator's saved phone (resolved server-side as
	// `selected_phone_number`). Create: fall back to the linked WhatsApp
	// template's default sender so the form opens fully pre-filled.
	const savedPhone =
		"selected_phone_number" in template
			? (template.selected_phone_number ?? null)
			: null
	const defaultSenderPhone =
		template.selected_whatsapp_template?.phone_number ?? null
	const phoneNumber = savedPhone ?? defaultSenderPhone ?? undefined

	// Seed the checkpoints editor only for templates that use them (payment /
	// overdue reminders), from the saved offsets or the template defaults.
	const checkpointOffsets = Array.isArray(derived?.checkpoints)
		? derived?.checkpoints
		: Array.isArray(template.defaults?.checkpoints)
			? (template.defaults?.checkpoints as Array<string>)
			: null

	// Seed the optional repeat window from the saved offsets. The editor fields
	// are always pre-filled with sensible defaults so enabling it is one click.
	const supportsIntervalWindow = template.configurable_fields?.some(
		(field) => field.key === "interval_window"
	)
	const savedIntervalWindow =
		supportsIntervalWindow && derived?.interval_window
			? derived.interval_window
			: null
	const enableIntervalWindow = Boolean(
		savedIntervalWindow &&
		(savedIntervalWindow.start_offset ||
			savedIntervalWindow.end_offset ||
			savedIntervalWindow.every)
	)

	// Seed the message-sequence editor: saved rows (each with its server-resolved
	// template) on edit, or a single starter row keyed to the linked template on
	// create. The first row fires on the trigger, so its wait is unused.
	const messages: Array<TTemplateMessageFormValue> =
		!templateSupportsMessageSequence(template)
			? []
			: Array.isArray(derived?.messages) && derived.messages.length > 0
				? derived.messages.map((message) => {
						const whatsappChannel = message.channels?.find(
							(channel) => channel.channel === "whatsapp"
						)

						const kind = (whatsappChannel?.kind ??
							"template") as TAutomationMessageKind
						const screen = whatsappChannel?.flow_screen

						return {
							kind,
							whatsapp_template:
								whatsappChannel?.whatsapp_template ?? undefined,
							body: whatsappChannel?.body ?? "",
							flow: whatsappChannel?.flow ?? null,
							flow_body: whatsappChannel?.flow_body ?? "",
							flow_cta: whatsappChannel?.flow_cta ?? "",
							// The screen is a bare id; the picker's option is
							// the id under both its keys until the list loads.
							flow_screen: screen ? { id: screen, title: screen } : null,
							flow_header: whatsappChannel?.flow_header ?? "",
							flow_footer: whatsappChannel?.flow_footer ?? "",
							quick_send: whatsappChannel?.quick_send ?? null,
							enable_wait: Boolean(message.wait_before),
							wait_before:
								parseOffsetString(message.wait_before) ??
								createOffsetFormValue({ amount: "1", unit: "days" }),
						}
					})
				: [
						{
							whatsapp_template:
								template.selected_whatsapp_template ?? undefined,
							enable_wait: false,
							wait_before: createOffsetFormValue({
								amount: "1",
								unit: "days",
							}),
						},
					]

	return {
		whatsapp_template: template.selected_whatsapp_template || undefined,
		phone_number: phoneNumber || undefined,
		checkpoints: checkpointOffsets
			? offsetsToCheckpoints(checkpointOffsets)
			: [],
		enable_interval_window: enableIntervalWindow,
		interval_start_offset:
			parseOffsetString(savedIntervalWindow?.start_offset) ??
			createOffsetFormValue({ amount: "0", unit: "hours" }),
		interval_end_offset:
			parseOffsetString(savedIntervalWindow?.end_offset) ??
			createOffsetFormValue({ amount: "3", unit: "days" }),
		interval_every:
			parseEveryString(savedIntervalWindow?.every) ?? createEveryFormValue(),
		send_window: {
			from: derived?.send_window?.from ?? "09:00",
			to: derived?.send_window?.to ?? "21:00",
		},
		messages,
		// Saved rules on edit; otherwise an empty group, so the editor opens
		// with nothing to undo rather than a blank rule to fill in.
		filters: derived?.filters
			? ruleGroupFormValueFromGroup(derived.filters)
			: createRuleGroupFormValue({}, { withInitialRule: false }),
		// Text inputs come back on edit under their own keys; on create they
		// start from the recipe's defaults, or empty.
		inputs: Object.fromEntries(
			templateTextFields(template).map((field) => {
				const saved = derived
					? derived[field.key]
					: template.defaults?.[field.key]

				return [
					field.key,
					typeof saved === "string" || typeof saved === "number"
						? String(saved)
						: "",
				]
			})
		),
		// On/off options come back on edit as booleans; on create, the
		// recipe's default, or off.
		toggles: Object.fromEntries(
			templateBooleanFields(template).map((field) => [
				field.key,
				(derived ? derived[field.key] : template.defaults?.[field.key]) ===
					true,
			])
		),
		// The server resolves each Flow field to the Flow itself, so the
		// picker opens on its name rather than on a bare id.
		flows: Object.fromEntries(
			templateTextFields(template)
				.filter((field) => field.type === "whatsapp_flow")
				.map((field) => [
					field.key,
					("selected_flows" in template
						? (template.selected_flows?.[field.key] ?? null)
						: null) as TWAFlow | null,
				])
		),
		// Likewise for a template the recipe filters on, so the picker opens
		// on the template's name.
		waTemplates: Object.fromEntries(
			templateTextFields(template)
				.filter((field) => field.type === "whatsapp_template")
				.map((field) => [
					field.key,
					("selected_templates" in template
						? (template.selected_templates?.[field.key] ?? null)
						: null) as TSelectedWATemplate | null,
				])
		),
	}
}

function templatePayload(
	values: TTemplateFormValues,
	{
		supportsIntervalWindow,
		supportsMessageSequence,
		sendsWhatsAppTemplate,
		needsPhoneNumber,
		textFields,
		booleanFields,
		requiresCheckpoints,
		supportsSendWindow,
		filterFields,
		supportsFilters,
	}: {
		supportsIntervalWindow: boolean
		supportsMessageSequence: boolean
		sendsWhatsAppTemplate: boolean
		needsPhoneNumber: boolean
		textFields: Array<TAutomationTemplateField>
		booleanFields: Array<TAutomationTemplateField>
		requiresCheckpoints: boolean
		supportsSendWindow: boolean
		supportsFilters: boolean
		filterFields: Array<TAutomationSourceField>
	}
): TAutomationTemplatePayload {
	const payload: TAutomationTemplatePayload = {}

	if (needsPhoneNumber) {
		payload.phone_number_id = Number(values.phone_number?.id)
	}

	for (const field of textFields) {
		const value = (values.inputs?.[field.key] ?? "").trim()

		if (value || field.required) {
			payload[field.key] = value
		}
	}

	for (const field of booleanFields) {
		payload[field.key] = values.toggles?.[field.key] ? 1 : 0
	}

	// Delivery content is either a channel-agnostic message sequence or a single
	// legacy WhatsApp template; scheduling (checkpoints / repeat window) layers on
	// top and is orthogonal to that choice. A recipe that sends nothing of its
	// own carries neither.
	if (!sendsWhatsAppTemplate) {
		// Nothing to send.
	} else if (supportsMessageSequence) {
		payload.messages = (values.messages || []).map((row) => ({
			// Any message may declare a wait; without one it sends as soon as the
			// previous step (or the trigger) completes.
			wait_before: row.enable_wait ? offsetToString(row.wait_before) : null,
			channels: [messageChannel(row)],
		}))
	} else {
		payload.whatsapp_template_id = Number(values.whatsapp_template?.id)
	}

	if (requiresCheckpoints) {
		payload.checkpoints = checkpointsToOffsets(values.checkpoints)
	}

	if (supportsIntervalWindow) {
		const startOffset = values.enable_interval_window
			? offsetToString(values.interval_start_offset)
			: null
		const endOffset = values.enable_interval_window
			? offsetToString(values.interval_end_offset)
			: null
		const every = values.enable_interval_window
			? everyToString(values.interval_every)
			: null

		// Send the full window when enabled and valid; otherwise null so the
		// backend clears any previously saved window on edit.
		payload.interval_window =
			startOffset && endOffset && every
				? { start_offset: startOffset, end_offset: endOffset, every }
				: null
	}

	if (
		supportsSendWindow &&
		values.send_window?.from &&
		values.send_window?.to
	) {
		payload.send_window = {
			from: values.send_window.from,
			to: values.send_window.to,
		}
	}

	if (supportsFilters) {
		// Always sent, empty group included: removing the last rule has to clear
		// the stored ones rather than leave the automation gated on them.
		payload.filters = buildRuleGroup(
			values.filters ||
				createRuleGroupFormValue({}, { withInitialRule: false }),
			filterFields
		)
	}

	return payload
}

// Read-only preview of the picked WhatsApp template — the message as it will
// arrive, rendered by the same endpoint the templates listing uses, so the
// header media, buttons and formatting are the real ones rather than a
// text-only approximation.
function ApprovedTemplatePreview({
	template,
	sourceKey,
}: {
	template?: TSelectedWATemplate
	/**
	 * The source the automation runs on. When the template's variables come
	 * from somewhere else, the preview says so — the save refuses it on the
	 * same terms.
	 */
	sourceKey?: string | null
}) {
	const [mode, setMode] = useState<"raw" | "sample">("raw")

	if (!template) {
		return null
	}

	const mismatch = waTemplateSourceMismatch(template, sourceKey)

	return (
		<Box borderWidth="1" rounded="md" padding="4">
			<Stack gap="3">
				<Inline gap="2" flexWrap="wrap" alignItems="center">
					<Text fontWeight="semibold">{template.name}</Text>
					{template.language ? <Badge>{template.language}</Badge> : null}
					{template.category ? <Badge>{template.category}</Badge> : null}
					{template.template_data_source_name ? (
						<Badge outlined>{template.template_data_source_name}</Badge>
					) : null}
					<Inline gap="1" marginLeft="auto">
						<Button
							type="button"
							size="sm"
							level={mode === "raw" ? "primary" : "tertiary"}
							onClick={() => setMode("raw")}
						>
							Variables
						</Button>
						<Button
							type="button"
							size="sm"
							level={mode === "sample" ? "primary" : "tertiary"}
							onClick={() => setMode("sample")}
						>
							Sample values
						</Button>
					</Inline>
				</Inline>
				{mismatch ? (
					<Text fontSize="sm" color="warning">
						{mismatch} — this automation can't fill them, so saving will be
						refused.
					</Text>
				) : null}
				<WATemplatePreviewBubble template={template} mode={mode} />
			</Stack>
		</Box>
	)
}

// A message's "wait before sending" editor. Waits always move forward, so only
// an amount + unit is exposed (the direction is implicitly "after"). The wait
// runs after the previous step — the trigger, for the first message.
function WaitBeforeField({
	name,
	isFirstItem,
}: {
	name: string
	isFirstItem?: boolean
}) {
	return (
		<Inline gap="2" alignItems="center" flexWrap="wrap">
			<Text fontSize="sm" color="muted">
				{isFirstItem ? "Wait" : "Then wait"}
			</Text>
			<Box style={{ maxWidth: "100px" }}>
				<TextInputField
					name={`${name}.amount`}
					type="number"
					min="1"
					size="sm"
				/>
			</Box>
			<Box>
				<SelectInputField name={`${name}.unit`} size="sm">
					<option value="minutes">Minutes</option>
					<option value="hours">Hours</option>
					<option value="days">Days</option>
				</SelectInputField>
			</Box>
			<Text fontSize="sm" color="muted">
				before sending this message.
			</Text>
		</Inline>
	)
}

// The message-sequence editor: an ordered list of WhatsApp messages. The first
// row sends on the trigger; each later row waits before sending. A recipe that
// answers a conversation sends from the number the customer wrote to; any
// other shares one sender phone, picked outside this field.
function MessageSequenceField({
	template,
	integratedAppId,
	waBusinessAccountId,
	onPrimaryTemplateChange,
}: {
	template: TAutomationTemplate
	integratedAppId?: number
	/**
	 * Scopes the template and Flow pickers: both belong to one business
	 * account, and one automation can only carry one account's.
	 */
	waBusinessAccountId?: number
	onPrimaryTemplateChange?: (
		next?: TAutomationTemplateWhatsAppTemplateOption
	) => void
}) {
	const form = useForm()
	const kinds = templateMessageKinds(template)
	const variables = template.template_data_source?.available_parameters || []

	return (
		<FieldArray<TTemplateMessageFormValue> name="messages">
			{({ fields }) => (
				<Stack gap="4">
					{fields.map((name, index) => (
						<Box key={name} borderWidth="1" rounded="md" padding="4">
							<Inline gap="3">
								<Inline
									justifyContent="center"
									alignItems="center"
									size="8"
									bgColor="subtle"
									rounded="full"
									borderWidth="1"
								>
									<Text fontWeight="semibold">{index + 1}</Text>
								</Inline>
								<Stack gap="3" flex="1">
									<GetFieldValue<boolean> name={`${name}.enable_wait`}>
										{({ value: waitEnabled }) => (
											<Stack gap="2">
												<Inline gap="1" flexWrap="wrap" alignItems="center">
													{waitEnabled ? (
														<WaitBeforeField
															name={`${name}.wait_before`}
															isFirstItem={index === 0}
														/>
													) : (
														<Text color="muted" fontSize="sm">
															{index === 0
																? "Sent immediately when the automation triggers."
																: "Sent as soon as the previous message goes out."}
														</Text>
													)}
													<Box fontSize="sm">
														<Button
															type="button"
															size="sm"
															level="tertiary"
															onClick={() => {
																if (
																	!waitEnabled &&
																	!fields.value?.[index]?.wait_before
																) {
																	form.change(
																		`${name}.wait_before`,
																		createOffsetFormValue({
																			amount: "1",
																			unit: "days",
																		})
																	)
																}
																form.change(`${name}.enable_wait`, !waitEnabled)
															}}
														>
															<Icons.Clock />
															{waitEnabled
																? "Remove wait"
																: index === 0
																	? "Delay the first message"
																	: "Add a wait before this"}
														</Button>
													</Box>
												</Inline>
											</Stack>
										)}
									</GetFieldValue>
									{kinds.length > 1 ? (
										<SelectInputField
											name={`${name}.kind`}
											label="Reply with"
											help="A template reaches the customer at any time. The others only work while the conversation is open, which it is for 24 hours after the customer last wrote."
										>
											{kinds.map((kind) => (
												<option key={kind} value={kind}>
													{MESSAGE_KIND_LABELS[kind]}
												</option>
											))}
										</SelectInputField>
									) : null}
									<GetFieldValue<TAutomationMessageKind> name={`${name}.kind`}>
										{({ value: kind }) =>
											// Keyed by kind so switching one unmounts the other.
											// Without it React keeps the picker instance, and the
											// options it already loaded for a form get drawn by
											// the template picker, which reads fields a form has
											// no answer for.
											(kind ?? "template") === "quick_send" ? (
												<Stack key="quick_send" gap="2">
													<SelectField
														select={SelectWAQuickSend}
														name={`${name}.quick_send`}
														label="Quick Send"
														placeholder="Select a quick send"
														required
														help="Its messages go out in order, from the number the customer wrote to."
													/>
												</Stack>
											) : (kind ?? "template") === "text" ? (
												<Stack key="text" gap="2">
													<TextAreaInputField
														name={`${name}.body`}
														label="Message"
														required
														placeholder="Thanks {{customer_name}}, where would you like to go?"
														help="Variables resolve the way they do in a template."
													/>
													<AvailableVariables variables={variables} />
												</Stack>
											) : (kind ?? "template") === "flow" ? (
												<Stack key="flow" gap="3">
													<SelectField
														select={SelectWAFlow}
														name={`${name}.flow`}
														label="Form"
														placeholder="Select a published Flow"
														required
														waBusinessAccountId={waBusinessAccountId}
														onChange={(value: unknown) => {
															const flow = (
																Array.isArray(value) ? value[0] : value
															) as TWAFlow | null
															form.change(`${name}.flow`, flow as never)
															// A screen belongs to one Flow, so a new
															// Flow starts with none picked.
															form.change(`${name}.flow_screen`, null as never)
														}}
													/>
													<GetFieldValue<TWAFlow | null> name={`${name}.flow`}>
														{({ value: flow }) => (
															<SelectField
																key={String(flow?.id ?? "none")}
																select={SelectWAFlowScreen}
																name={`${name}.flow_screen`}
																label="Opening Screen"
																required
																autoSelectFirst
																flowId={flow?.id}
																waBusinessAccountId={waBusinessAccountId}
																placeholder={
																	flow
																		? "Select the opening screen"
																		: "Pick the form first"
																}
																disabled={!flow}
															/>
														)}
													</GetFieldValue>
													<TextAreaInputField
														name={`${name}.flow_body`}
														label="Message"
														required
														help="The bubble the button sits under. Variables resolve the way they do in a template."
													/>
													<AvailableVariables variables={variables} />
													<TextInputField
														name={`${name}.flow_cta`}
														label="Button Label"
														required
														maxLength={20}
													/>
												</Stack>
											) : (
												<Stack key="template" gap="3">
													<SelectField
														select={SelectWATemplate}
														name={`${name}.whatsapp_template`}
														label="WhatsApp Template"
														// Every template is offered, whatever recipe it
														// was written for: one template serves as many
														// automations as want it. Those this automation
														// cannot fill are marked, not hidden, and the
														// save refuses them.
														unscoped
														expectedSourceKey={template.parameters_source_key}
														required
														includeDrafts
														creatable
														automationTemplate={template}
														integratedAppId={integratedAppId}
														waBusinessAccountId={waBusinessAccountId}
														createOptionLabel={(query: string) =>
															query
																? `Create new template "${query}"`
																: "Create new template"
														}
														onChange={(value: unknown) => {
															const next = (
																Array.isArray(value) ? value[0] : value
															) as
																| TAutomationTemplateWhatsAppTemplateOption
																| undefined
															form.change(
																`${name}.whatsapp_template`,
																next as never
															)
															if (index !== 0) {
																return
															}

															// The sender phone is filtered by the first
															// template's business account, so reset it
															// when that changes.
															onPrimaryTemplateChange?.(next)
															// Every message is answered on one business
															// account, so a later pick from the account
															// left behind is cleared rather than saved
															// and refused.
															clearMessagesOfOtherAccounts(
																form,
																fields.value || [],
																next?.wa_business_account_id ?? undefined
															)
														}}
													/>
													<GetFieldValue<TSelectedWATemplate>
														name={`${name}.whatsapp_template`}
													>
														{({ value: selected }) => (
															<ApprovedTemplatePreview
																template={selected}
																sourceKey={template.parameters_source_key}
															/>
														)}
													</GetFieldValue>
												</Stack>
											)
										}
									</GetFieldValue>
								</Stack>
								<Box>
									<Button
										type="button"
										level="tertiary"
										disabled={fields.length === 1}
										onClick={() => fields.remove(index)}
									>
										<Icons.Trash />
									</Button>
								</Box>
							</Inline>
						</Box>
					))}
					<Box>
						<Button
							type="button"
							level="secondary"
							size="sm"
							onClick={() =>
								fields.push({
									enable_wait: true,
									wait_before: createOffsetFormValue({
										amount: "1",
										unit: "days",
									}),
								})
							}
						>
							<Icons.Plus /> Add Message
						</Button>
					</Box>
				</Stack>
			)}
		</FieldArray>
	)
}

export function AutomationTemplateForm({
	template,
	derived,
	loading,
	onSubmit,
	onCancel,
	submitLabel,
	integratedAppId,
}: {
	template: TAutomationTemplate
	derived?: TAutomationTemplateAutomation["input"] | null
	loading?: boolean
	onSubmit: (payload: TAutomationTemplatePayload) => Promise<unknown>
	onCancel: () => unknown
	submitLabel: string
	/**
	 * When provided, the inline "Create new template" flow opens the editor
	 * directly under this integration. When omitted (e.g. on the global
	 * /automations/new page), the operator is asked to pick a WhatsApp
	 * integration first; if they have exactly one, the picker auto-resolves.
	 */
	integratedAppId?: number
}) {
	const [initialValues] = useState<TTemplateFormValues>(() =>
		buildInitialValues(template, derived)
	)

	const requiresCheckpoints = template.configurable_fields.some(
		(field) => field.key === "checkpoints"
	)
	const supportsIntervalWindow = template.configurable_fields.some(
		(field) => field.key === "interval_window"
	)
	const supportsSendWindow = template.configurable_fields.some(
		(field) => field.key === "send_window"
	)
	const supportsMessageSequence = templateSupportsMessageSequence(template)
	const sendsWhatsAppTemplate = templateSendsWhatsAppTemplate(template)
	const needsPhoneNumber = templateNeedsPhoneNumber(template)
	const textFields = templateTextFields(template)
	const detailFields = templateDetailFields(template)
	const booleanFields = templateBooleanFields(template)
	const promotedFilterFields = templatePromotedFilterFields(template)
	const supportsFilters = templateSupportsFilters(template)
	const filterFields = template.filter_source?.filter_fields || []

	return (
		<Form<TTemplateFormValues>
			key={`${template.key}-${JSON.stringify(derived || template.defaults || {})}`}
			initialValues={initialValues}
			validate={validateFormValues(templateSchema(template))}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(
					templatePayload(values, {
						requiresCheckpoints,
						supportsIntervalWindow,
						supportsMessageSequence,
						sendsWhatsAppTemplate,
						needsPhoneNumber,
						textFields,
						booleanFields,
						supportsSendWindow,
						supportsFilters,
						filterFields,
					})
				)
			})}
			subscription={{ submitting: true, values: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ submitting, handleSubmit, values, form }) => {
				// One input per configurable field, whether it is drawn among
				// the details or lifted up beside the conditions. A picker
				// keeps the picked record for display and writes its id to the
				// input the recipe actually reads.
				const renderConfigurableField = (field: TAutomationTemplateField) =>
					field.type === "whatsapp_flow" ? (
						<SelectField
							key={field.key}
							select={SelectWAFlow}
							name={`flows.${field.key}`}
							label={field.label}
							placeholder="Select a published Flow"
							required={field.required}
							help={field.description}
							onChange={(value: unknown) => {
								const flow = (
									Array.isArray(value) ? value[0] : value
								) as TWAFlow | null
								form.change(
									`flows.${field.key}` as never,
									(flow ?? null) as never
								)
								form.change(
									`inputs.${field.key}` as never,
									String(flow?.id ?? "") as never
								)
							}}
						/>
					) : field.type === "whatsapp_template" ? (
						<SelectField
							key={field.key}
							select={SelectWATemplate}
							name={`waTemplates.${field.key}`}
							label={field.label}
							placeholder="Select a template"
							required={field.required}
							help={field.description}
							// The template being filtered on was sent by any
							// recipe or none, so it carries no source or sender
							// scope of its own.
							unscoped
							onChange={(value: unknown) => {
								const waTemplate = (
									Array.isArray(value) ? value[0] : value
								) as TSelectedWATemplate | null
								form.change(
									`waTemplates.${field.key}` as never,
									(waTemplate ?? null) as never
								)
								form.change(
									`inputs.${field.key}` as never,
									String(waTemplate?.id ?? "") as never
								)
								// A button named on the old template may not
								// exist on this one, and a rule on a button
								// that cannot be tapped never matches.
								const labels = quickReplyLabels(waTemplate)
								for (const dependent of textFields) {
									if (dependent.buttons_from !== field.key) {
										continue
									}
									const picked = (values.inputs?.[dependent.key] ?? "").trim()
									if (picked && !labels.includes(picked)) {
										form.change(`inputs.${dependent.key}` as never, "" as never)
									}
								}
							}}
						/>
					) : field.buttons_from ? (
						<QuickReplyField
							key={field.key}
							field={field}
							waTemplate={values.waTemplates?.[field.buttons_from]}
							value={values.inputs?.[field.key] ?? ""}
						/>
					) : field.type === "textarea" ? (
						<TextAreaInputField
							key={field.key}
							name={`inputs.${field.key}`}
							label={field.label}
							placeholder={field.placeholder}
							required={field.required}
							help={field.description}
						/>
					) : (
						<TextInputField
							key={field.key}
							name={`inputs.${field.key}`}
							label={field.label}
							placeholder={field.placeholder}
							required={field.required}
							help={field.description}
						/>
					)

				return (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="6">
							<Stack gap="6">
								{/* When it runs comes before what it sends: the
								    conditions decide which records ever reach the
								    steps below. */}
								{supportsFilters ? (
									<>
										<Grid gap="4">
											<Col sm={12} md={3}>
												<Stack gap="1">
													<Heading fontWeight="semibold" fontSize="md">
														Conditions
													</Heading>
													<Text color="muted" fontSize="sm">
														Only run for records matching these rules. Add none
														and it runs for every one.
													</Text>
												</Stack>
											</Col>
											<Col>
												<Stack gap="4">
													{promotedFilterFields.map(renderConfigurableField)}
													{promotedFilterFields.length ? (
														<Text color="muted" fontSize="sm">
															Narrow it further with the rules below.
														</Text>
													) : null}
													<GetFieldValue<TAutomationRuleGroupFormValue> name="filters">
														{({ value: group }) => (
															<RuleGroupEditor
																group={
																	group ||
																	createRuleGroupFormValue(
																		{},
																		{ withInitialRule: false }
																	)
																}
																source={template.filter_source}
																fields={filterFields}
																prefix="filters"
																allowEmpty
																onChange={(nextGroup) =>
																	form.change("filters", nextGroup)
																}
															/>
														)}
													</GetFieldValue>
												</Stack>
											</Col>
										</Grid>
										<Divider marginY="0" />
									</>
								) : null}
								{detailFields.length ||
								booleanFields.length ||
								(needsPhoneNumber && !sendsWhatsAppTemplate) ? (
									<Grid gap="4">
										<Col sm={12} md={3}>
											<Stack gap="1">
												<Heading as="h3">Details</Heading>
												<Text color="muted" fontSize="sm">
													What this automation needs to know.
												</Text>
											</Stack>
										</Col>
										<Col>
											<Stack gap="4">
												{booleanFields.map((field) => (
													<SwitchInputField
														key={field.key}
														name={`toggles.${field.key}`}
														label={field.label}
														help={field.description}
													/>
												))}
												{detailFields.map(renderConfigurableField)}
												{needsPhoneNumber && !sendsWhatsAppTemplate ? (
													<SelectField
														select={SelectWAPhoneNumber}
														name="phone_number"
														label="Sender Phone Number"
														required
														allowAll
														help="The number the message goes out from. For a reply into a conversation it must be the number the customer wrote to."
													/>
												) : null}
											</Stack>
										</Col>
									</Grid>
								) : null}
								{sendsWhatsAppTemplate ? (
									<>
										<Stack gap="1">
											<Heading as="h3">Template Setup</Heading>
											{supportsMessageSequence ? (
												<Text color="muted">
													{needsPhoneNumber
														? "Add the WhatsApp templates to send, in order. Every message uses the one sender number below."
														: "Add the messages to send, in order. They go out from the number the customer wrote to, and a template or form limits this automation to conversations on its business account."}
												</Text>
											) : (
												<Text color="muted">
													Select the approved WhatsApp template first. Sender
													numbers are filtered to that template's business
													account.
												</Text>
											)}
										</Stack>
										{supportsMessageSequence ? (
											<>
												<Grid gap="4">
													<Col sm={12} md={3}>
														<Stack gap="1">
															<Text fontWeight="semibold" fontSize="md">
																Messages
															</Text>
															<Text color="muted" fontSize="sm">
																Sent in order on a single trigger. Each message
																goes out as soon as the previous step finishes,
																unless you add a wait before it.
															</Text>
														</Stack>
													</Col>
													<Col>
														<MessageSequenceField
															template={template}
															integratedAppId={integratedAppId}
															waBusinessAccountId={messageBusinessAccountId(
																values
															)}
															onPrimaryTemplateChange={
																needsPhoneNumber
																	? (next) =>
																			form.change(
																				"phone_number",
																				(next?.phone_number ??
																					undefined) as never
																			)
																	: undefined
															}
														/>
													</Col>
												</Grid>
												{needsPhoneNumber ? (
													<>
														<Divider marginY="0" />
														<Grid gap="4">
															<Col sm={12} md={3}>
																<Stack gap="1">
																	<Text fontWeight="semibold" fontSize="md">
																		Sender Phone Number
																	</Text>
																	<Text color="muted" fontSize="sm">
																		{values.messages?.[0]?.whatsapp_template
																			? "Only sender numbers in the first message's business account are shown."
																			: "Pick the first message's template to load its available sender numbers."}
																	</Text>
																</Stack>
															</Col>
															<Col>
																<SelectField
																	select={SelectWAPhoneNumber}
																	name="phone_number"
																	label="Sender Phone Number"
																	required
																	waTemplateId={
																		values.messages?.[0]?.whatsapp_template?.id
																	}
																/>
															</Col>
														</Grid>
													</>
												) : null}
											</>
										) : (
											<>
												<SelectField
													select={SelectWATemplate}
													name="whatsapp_template"
													label="WhatsApp Template"
													unscoped
													expectedSourceKey={template.parameters_source_key}
													required
													includeDrafts
													creatable
													automationTemplate={template}
													integratedAppId={integratedAppId}
													createOptionLabel={(query: string) =>
														query
															? `Create new template "${query}"`
															: "Create new template"
													}
													help="Pick an existing template or create a new one inline."
													onChange={(value: unknown) => {
														// Reset the phone whenever the template changes — the
														// new template's WABA may not include the previously
														// chosen phone.
														const nextTemplate = (
															Array.isArray(value) ? value[0] : value
														) as
															| TAutomationTemplateWhatsAppTemplateOption
															| undefined
														form.change(
															"whatsapp_template",
															nextTemplate as never
														)
														form.change(
															"phone_number",
															(nextTemplate?.phone_number ?? undefined) as never
														)
													}}
												/>
												{needsPhoneNumber ? (
													<SelectField
														select={SelectWAPhoneNumber}
														name="phone_number"
														label="Sender Phone Number"
														required
														waTemplateId={values.whatsapp_template?.id}
														help={
															values.whatsapp_template
																? "Only sender numbers in the template's business account are shown."
																: "Pick a WhatsApp template first to load its available sender numbers."
														}
													/>
												) : null}
											</>
										)}
									</>
								) : null}
								{/* Scheduling controls are orthogonal to the delivery
								    content, so they render for both content modes. */}
								{requiresCheckpoints ? (
									<>
										<Divider marginY="0" />
										<Grid gap="4">
											<Col sm={12} md={3}>
												<Stack gap="1">
													<Heading fontWeight="semibold" fontSize="md">
														Checkpoints
													</Heading>
													<Text color="muted" fontSize="sm">
														Each checkpoint creates one independent run relative
														to the due date
													</Text>
												</Stack>
											</Col>
											<Col>
												<CheckpointsField
													name="checkpoints"
													previewLabel="the due date"
												/>
											</Col>
										</Grid>
									</>
								) : null}
								{supportsIntervalWindow ? (
									<>
										<Divider marginY="0" />
										<Grid gap="4">
											<Col sm={12} md={3}>
												<Stack gap="1">
													<Heading fontWeight="semibold" fontSize="md">
														Repeat Window
													</Heading>
													<Text color="muted" fontSize="sm">
														Optional. Use this only when you want repeated runs
														between two offsets.
													</Text>
												</Stack>
											</Col>
											<Col>
												<IntervalWindowField previewLabel="the due date" />
											</Col>
										</Grid>
									</>
								) : null}
								{supportsSendWindow ? (
									<>
										<Divider marginY="0" />
										<Grid gap="4">
											<Col sm={12} md={3}>
												<Stack gap="1">
													<Heading fontWeight="semibold" fontSize="md">
														Send Window
													</Heading>
													<Text color="muted" fontSize="sm">
														Local hours reminders may go out; others move to the
														next window open.
													</Text>
												</Stack>
											</Col>
											<Col>
												<SendWindowField />
											</Col>
										</Grid>
									</>
								) : null}
							</Stack>
							<Divider marginY="0" />
							<Grid gap="0">
								<Col sm={12} md={{ offset: 3, span: 9 }}>
									<Inline gap="4" flexWrap="wrap" paddingLeft={{ md: "4" }}>
										<Button
											type="submit"
											level="primary"
											disabled={submitting || loading}
										>
											{submitLabel}
										</Button>
										<Button type="button" level="secondary" onClick={onCancel}>
											Cancel
										</Button>
									</Inline>
								</Col>
							</Grid>
						</Stack>
					</form>
				)
			}}
		</Form>
	)
}

/**
 * The catalogue's state lives in the URL: `source` is the open tab,
 * `template` the picked recipe, `mode=custom` the builder. Cards and tabs
 * are plain links, so a pick is a history entry and the back button walks
 * the steps.
 */
type TCatalogueQuery = {
	source?: string
	template?: string
	mode?: string
}

function useCatalogueQuery(): [string, TCatalogueQuery] {
	const location = useLocation()
	return [
		location.pathname,
		useMemo(
			() => searchToQuery<TCatalogueQuery>(location.search),
			[location.search]
		),
	]
}

function TemplateCard({
	title,
	description,
	to,
	query,
}: {
	title: React.ReactNode
	description?: React.ReactNode
	to: string
	query: TCatalogueQuery
}) {
	return (
		<Link
			to={to}
			query={query}
			display="block"
			width="full"
			height="full"
			textAlign="left"
			rounded="md"
			borderWidth="1"
			bgColor={{ default: "default", hover: "subtle" }}
			padding="4"
			cursor="pointer"
			color="default"
		>
			<Stack gap="2" height="full">
				<Inline gap="3" alignItems="start">
					<Stack gap="1" flex="1">
						<Text fontWeight="semibold">{title}</Text>
						{description ? (
							<Text fontSize="sm" color="muted">
								{description}
							</Text>
						) : null}
					</Stack>
				</Inline>
			</Stack>
		</Link>
	)
}

function SectionHeading({ children }: { children: React.ReactNode }) {
	return (
		<Inline gap="2" alignItems="center">
			<Text fontWeight="semibold" color="muted" fontSize="sm">
				{children}
			</Text>
			<Box flex="1">
				<Divider marginY="0" />
			</Box>
		</Inline>
	)
}

function TemplateCardGrid({
	templates,
	to,
	source,
}: {
	templates: Array<TAutomationTemplateSummary>
	to: string
	source?: string
}) {
	return (
		<Grid padding="2">
			{templates.map((template) => (
				<Col key={template.key} xs={12} md={6} lg={4} paddingX="0">
					<Stack padding="2" height="full">
						<TemplateCard
							title={template.name}
							description={template.description}
							to={to}
							query={{ source, template: template.key }}
						/>
					</Stack>
				</Col>
			))}
		</Grid>
	)
}

function CatalogueTab({
	active,
	to,
	query,
	children,
}: {
	active: boolean
	to: string
	query: TCatalogueQuery
	children: React.ReactNode
}) {
	return (
		<Box as="li" className={active ? "active" : undefined}>
			<Link to={to} query={query}>
				{children}
			</Link>
		</Box>
	)
}

/**
 * The recipe catalogue as cards behind a tab per record type the recipes
 * work from, "All" first, with a last card for building an automation from
 * scratch. Tabs and cards are links into the URL query. Cards carry the
 * summary only; picking one is what reads the recipe in full.
 */
export function TemplateCatalog({
	templates,
	allowCustom,
}: {
	templates: Array<TAutomationTemplateSummary>
	/** Offer the fully customisable builder as the last card. */
	allowCustom?: boolean
}) {
	const [pathname, query] = useCatalogueQuery()
	const activeTab = query.source

	const groups = useMemo(() => {
		const list: Array<{
			key: string
			name: string
			templates: Array<TAutomationTemplateSummary>
		}> = []

		for (const template of templates) {
			const key = template.source_key
			const group = list.find((item) => item.key === key)
			if (group) {
				group.templates.push(template)
			} else {
				list.push({
					key,
					name: template.source_name || template.source_key,
					templates: [template],
				})
			}
		}

		return list
	}, [templates])

	const activeGroup = groups.find((group) => group.key === activeTab)

	const customCard = allowCustom ? (
		<Stack gap="3">
			<SectionHeading>Or build your own</SectionHeading>
			<Grid gap="4">
				<Col xs={12} sm={6} lg={4}>
					<TemplateCard
						title="Custom Automation"
						description="Start from scratch: pick the trigger, add conditions and lay out the steps yourself."
						to={pathname}
						query={{ mode: "custom" }}
					/>
				</Col>
			</Grid>
		</Stack>
	) : null

	return (
		<Tabs borderWidth="1" rounded="md" overflow="hidden">
			<TabsList>
				<CatalogueTab active={!activeGroup} to={pathname} query={{}}>
					All{" "}
					<Text as="span" color="muted" fontSize="sm">
						({templates.length})
					</Text>
				</CatalogueTab>
				{groups.map((group) => (
					<CatalogueTab
						key={group.key}
						active={activeGroup?.key === group.key}
						to={pathname}
						query={{ source: group.key }}
					>
						{group.name}{" "}
						<Text as="span" color="muted" fontSize="sm">
							({group.templates.length})
						</Text>
					</CatalogueTab>
				))}
			</TabsList>
			<TabContent>
				<Stack gap="6">
					<TemplateCardGrid
						templates={activeGroup ? activeGroup.templates : templates}
						to={pathname}
						source={activeGroup?.key}
					/>
					{!activeGroup ? <Box padding="4">{customCard}</Box> : null}
				</Stack>
			</TabContent>
		</Tabs>
	)
}

/**
 * Two steps: pick a card from the catalogue, then fill in the recipe. The
 * catalogue is summaries only; the pick reads the recipe in full (fields,
 * defaults, linked WhatsApp template) and seeds the form from it. The custom
 * card, when offered, swaps the recipe form for the full builder.
 *
 * Shared by the generic /automations/new flow and the integration-scoped
 * /integrations/:id/whatsapp/automations/new flow: the host supplies the
 * catalogue and the routing, this handles the rest.
 */
export function AutomationTemplateCreator({
	templates,
	loadTemplateDetails,
	onCreated,
	onCancel,
	intro,
	integratedAppId,
	allowCustom,
}: {
	templates: Array<TAutomationTemplateSummary>
	loadTemplateDetails?: (key: string) => Promise<TAutomationTemplate>
	onCreated: (automationId: number) => void
	onCancel: () => void
	intro?: React.ReactNode
	/** Forwarded to the form so the inline create-template flow can skip the
	 *  integration picker on the integration-scoped pages. */
	integratedAppId?: number
	/** Offer the fully customisable builder as the last card. */
	allowCustom?: boolean
}) {
	const xhr = useXHR()
	const [pathname, query] = useCatalogueQuery()
	const custom = allowCustom && query.mode === "custom"
	const picked = useMemo(
		() =>
			query.template
				? templates.find((template) => template.key === query.template) || null
				: null,
		[templates, query.template]
	)
	// The loaded detail, remembered with the key it was read for: a detail
	// for another key is stale, and "no detail for this key yet" is loading.
	const [loaded, setLoaded] = useState<{
		key: string
		detail?: TAutomationTemplate
		error?: string
	} | null>(null)

	const pickedKey = picked?.key
	const current = pickedKey && loaded?.key === pickedKey ? loaded : null
	const detail = current?.detail
	const detailError = current?.error
	const detailLoading = Boolean(pickedKey) && !current

	// The picked recipe is read in full whenever the URL names a new one.
	useEffect(() => {
		if (!pickedKey) {
			return
		}
		let mounted = true
		;(loadTemplateDetails
			? loadTemplateDetails(pickedKey)
			: XHR(xhr)
					.showTemplate(pickedKey)
					.then((response) => response.data)
		)
			.then((detail) => {
				if (mounted) setLoaded({ key: pickedKey, detail })
			})
			.catch((error) => {
				if (mounted)
					setLoaded({
						key: pickedKey,
						error:
							(error as Error)?.message ||
							"Unable to load the automation template.",
					})
			})
		return () => {
			mounted = false
		}
	}, [pickedKey, loadTemplateDetails, xhr])

	// Back to the catalogue, on the tab the pick was made from.
	const backLink = (
		<ButtonLink
			to={pathname}
			query={{ source: query.source }}
			level="tertiary"
			size="sm"
		>
			<Icons.ArrowLeft /> Templates
		</ButtonLink>
	)

	if (custom) {
		return (
			<Stack gap="4">
				<Inline gap="3" alignItems="center" flexWrap="wrap">
					{backLink}
				</Inline>
				<Stack gap="2">
					<Heading as="h2">Custom Automation</Heading>
					<Text color="muted">
						Build a custom automation with your own trigger, filters and ordered
						steps.
					</Text>
				</Stack>
				<AutomationDefinitionForm
					onSuccess={(savedAutomation) => onCreated(savedAutomation.id)}
					onCancel={onCancel}
				/>
			</Stack>
		)
	}

	if (!picked) {
		return (
			<Stack gap="6">
				{intro ?? (
					<Stack gap="2">
						<Heading as="h2">Choose an Automation Template</Heading>
						<Text color="muted">
							Pick a ready-made automation and fill in the details, or build one
							from scratch.
						</Text>
					</Stack>
				)}
				<TemplateCatalog templates={templates} allowCustom={allowCustom} />
			</Stack>
		)
	}

	return (
		<Stack gap="4">
			<Inline gap="3" flexWrap="wrap" collapseBelow="sm">
				<Stack justifyContent="center" alignItems="start">
					{backLink}
				</Stack>
				<Stack gap="2" flex="1">
					<Inline gap="2" alignItems="center" flexWrap="wrap">
						<Heading as="h2">{picked.name}</Heading>
						<Badge>{picked.channel}</Badge>
					</Inline>
					<Text color="muted">{picked.description}</Text>
				</Stack>
			</Inline>
			<Divider marginY="0" />
			{detailLoading ? (
				<Spinner padding="4" alignCenter />
			) : detailError ? (
				<Alert title="Unable to load the template" status="warning">
					{detailError}
				</Alert>
			) : detail ? (
				<AutomationTemplateForm
					// Keyed on the loaded detail so `buildInitialValues` runs
					// with `selected_whatsapp_template` in hand.
					key={`${detail.key}-${detail.selected_whatsapp_template?.id ?? "none"}`}
					template={detail}
					integratedAppId={integratedAppId}
					onSubmit={async (payload) => {
						const response = await XHR(xhr).createFromTemplate(
							detail.key,
							payload
						)
						showSnackbar("Automation created successfully.")
						onCreated(response.data.id)
					}}
					onCancel={onCancel}
					submitLabel="Create Automation"
				/>
			) : null}
		</Stack>
	)
}

export function AutomationTemplateDefinitionPage({
	automationId,
}: {
	automationId?: string
}) {
	const xhr = useXHR()
	const navigate = useNavigate()
	const isEdit = Boolean(automationId)
	const [loading, setLoading] = useState(true)
	const [templates, setTemplates] = useState<Array<TAutomationTemplateSummary>>(
		[]
	)
	const [selectedTemplate, setSelectedTemplate] =
		useState<TAutomationTemplate | null>(null)
	const [derivedTemplate, setDerivedTemplate] =
		useState<TAutomationTemplateAutomation | null>(null)
	const [deriveError, setDeriveError] = useState<string | null>(null)

	useEffect(() => {
		let mounted = true

		async function load() {
			setLoading(true)
			setDeriveError(null)

			try {
				if (isEdit && automationId) {
					const response = await XHR(xhr).showAutomationTemplate(automationId)

					if (!mounted) return

					setSelectedTemplate(response.data)
					setDerivedTemplate(response.data)
					return
				}

				const response = await XHR(xhr).getTemplates()

				if (!mounted) return

				setTemplates(response.data)
			} catch (error) {
				if (!mounted) return

				setDeriveError(
					(error as Error)?.message || "Unable to load the automation template."
				)
			} finally {
				if (mounted) {
					setLoading(false)
				}
			}
		}

		load()

		return () => {
			mounted = false
		}
	}, [automationId, isEdit, xhr])

	const title = isEdit ? "Edit Automation" : "New Automation"
	const definitionIndexPath = generatePath("/org/automations/definitions")
	const breadcrumbItems: Array<[string, string]> = [
		[definitionIndexPath, "Automations"],
		[definitionIndexPath, "Definitions"],
	]

	if (automationId) {
		breadcrumbItems.push([
			generatePath("/automations/:automationId", { automationId }),
			"Automation",
		])
	}

	breadcrumbItems.push(["", isEdit ? "Edit" : "New"])

	return (
		<>
			<Helmet>
				<title>{title}</title>
			</Helmet>
			<Breadcrumbs title={title} items={breadcrumbItems} />
			<Container paddingY="6" bgColor="default">
				{loading ? (
					<Spinner padding="4" alignCenter />
				) : deriveError ? (
					<Stack gap="4">
						<Alert title="Template editing unavailable" status="warning">
							{deriveError}
						</Alert>
						<ButtonLink to={definitionIndexPath} level="secondary">
							Back to Definitions
						</ButtonLink>
					</Stack>
				) : !isEdit ? (
					<AutomationTemplateCreator
						templates={templates}
						allowCustom
						onCreated={(id) =>
							navigate("/automations/:automationId", {
								params: { automationId: String(id) },
								replace: true,
							})
						}
						onCancel={() =>
							navigate("/org/automations/definitions", {
								replace: true,
							})
						}
					/>
				) : selectedTemplate && derivedTemplate ? (
					<Stack gap="6">
						<Stack gap="2">
							<Inline gap="3" alignItems="center" flexWrap="wrap">
								<Heading as="h2">Editing "{selectedTemplate.name}"</Heading>
							</Inline>
							<Text color="muted">{selectedTemplate.description}</Text>
						</Stack>
						<AutomationTemplateForm
							template={selectedTemplate}
							derived={derivedTemplate.input}
							onSubmit={async (payload) => {
								const response = await XHR(xhr).updateFromTemplate(
									automationId as string,
									payload
								)
								showSnackbar("Automation updated successfully.")
								navigate("/automations/:automationId", {
									params: {
										automationId: String(response.data.id),
									},
									replace: true,
								})
							}}
							onCancel={() =>
								navigate("/automations/:automationId", {
									params: {
										automationId: automationId as string,
									},
									replace: true,
								})
							}
							submitLabel="Update Automation"
						/>
					</Stack>
				) : (
					<Alert title="Template unavailable" status="warning">
						Unable to load this automation template.
					</Alert>
				)}
			</Container>
		</>
	)
}
